import React, { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { HouseholdMembership, PartyAddress } from "../../../types/types";
import { useStaticData } from "../../../../context/StaticDataContext";
import { RELATIONSHIP_CATEGORIES } from "../../../../constants/constants";
import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "../../../../css/buttons.css";

interface Props {
  value: HouseholdMembership[];
  onChange: (memberships: HouseholdMembership[]) => void;
  defaultLastName?: string;
  partyAddresses: PartyAddress[];
  adminMode?: boolean;
}

const HouseholdCard: React.FC<Props> = ({
  value,
  onChange,
  defaultLastName,
  partyAddresses,
  adminMode = true,
}) => {
  const { data } = useStaticData();
  const relationshipTypes =
    data?.relationshipTypes?.[RELATIONSHIP_CATEGORIES.HOUSEHOLD] || [];

  const existing = value?.[0] ?? null;
  const primaryAddress = partyAddresses.find((a) => a.isPrimary);

  const createDefaultMembership = useCallback((): HouseholdMembership => {
    const addressId =
      primaryAddress?.address?.addressId && primaryAddress.address.addressId > 0
        ? primaryAddress.address.addressId
        : null;

    return {
      householdMemberId: 0,
      householdId: 0,
      relationshipTypeId: undefined,
      isHeadOfHousehold: true,
      household: {
        householdId: 0,
        name: defaultLastName ? `${defaultLastName} Household` : "New Household",
        addressId,
        notes: "",
        totalAdults: 0,
        totalChildren: 0,
      },
    };
  }, [defaultLastName, primaryAddress]);

  const normaliseMembership = (
    nextMembership: HouseholdMembership,
  ): HouseholdMembership => ({
    householdMemberId: nextMembership.householdMemberId ?? 0,
    householdId: nextMembership.household?.householdId ?? 0,
    relationshipTypeId: nextMembership.relationshipTypeId,
    isHeadOfHousehold: true,
    household: nextMembership.household
      ? {
          ...nextMembership.household,
          householdId: nextMembership.household.householdId ?? 0,
          addressId:
            nextMembership.household.addressId &&
            nextMembership.household.addressId > 0
              ? nextMembership.household.addressId
              : null,
        }
      : undefined,
  });

  const [membership, setMembership] = useState<HouseholdMembership>(
    createDefaultMembership,
  );
  const [totalAdultsInput, setTotalAdultsInput] = useState("");
  const [totalChildrenInput, setTotalChildrenInput] = useState("");

  /* =========================
     Sync from parent
  ========================= */
  useEffect(() => {
    if (existing) {
      setMembership({
        householdMemberId: existing.householdMemberId ?? 0,
        householdId: existing.household?.householdId ?? 0,
        relationshipTypeId: existing.relationshipTypeId,
        isHeadOfHousehold: true,
        household: existing.household
          ? {
              ...existing.household,
              householdId: existing.household.householdId ?? 0,
              addressId:
                existing.household.addressId && existing.household.addressId > 0
                  ? existing.household.addressId
                  : null,
            }
          : undefined,
      });
      setTotalAdultsInput(String(existing.household?.totalAdults ?? ""));
      setTotalChildrenInput(String(existing.household?.totalChildren ?? ""));
    } else {
      setMembership(createDefaultMembership());
      setTotalAdultsInput("");
      setTotalChildrenInput("");
    }
  }, [createDefaultMembership, existing]);

  /* =========================
     Auto-save
  ========================= */
  const updateMembership = (nextMembership: HouseholdMembership) => {
    setMembership(nextMembership);
    onChange([normaliseMembership(nextMembership)]);
  };

  const handleDelete = () => {
    onChange([]);
    setMembership(createDefaultMembership());
  };

  if (!data) return null;

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-row">
          <div>
            <div className="card-title">
              {adminMode
                ? membership.household?.name || "Household"
                : "Household details"}
            </div>
          </div>

          {adminMode && existing && (
            <button type="button" className="icon-button" onClick={handleDelete}>
              <DeleteIcon />
            </button>
          )}
        </div>

      </div>

      {/* =========================
         Household Form
      ========================= */}
      {membership.household && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Total Adults</label>
              <input
                type="number"
                className="form-input"
                min="1"
                required
                value={totalAdultsInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setTotalAdultsInput(nextValue);
                  updateMembership({
                    ...membership,
                    household: {
                      ...membership.household!,
                      totalAdults: nextValue === "" ? 0 : Number(nextValue),
                    },
                  });
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Total Children</label>
              <input
                type="number"
                className="form-input"
                min="0"
                value={totalChildrenInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setTotalChildrenInput(nextValue);
                  updateMembership({
                    ...membership,
                    household: {
                      ...membership.household!,
                      totalChildren: nextValue === "" ? 0 : Number(nextValue),
                    },
                  });
                }}
              />
            </div>
          </div>

          {adminMode && (
            <div className="form-group">
              <label className="form-label">Relationship</label>
              <select
                className="form-input"
                value={membership.relationshipTypeId ?? ""}
                onChange={(e) =>
                  updateMembership({
                    ...membership,
                    relationshipTypeId: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <option value="">-- Select --</option>
                {relationshipTypes.map((relationshipType) => (
                  <option
                    key={relationshipType.relationshipTypeId}
                    value={relationshipType.relationshipTypeId}
                  >
                    {relationshipType.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default HouseholdCard;
